import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Mail, Copy, Loader2 } from "lucide-react";

const smsResult = (d) => d.simulated
  ? "Not sent: texting isn't live for this number yet (simulated). Nothing reached the recipient."
  : `Handed to Telnyx (status: ${d.status}) from ${d.from}. Delivery confirmation shows in Messages.`;
const emailResult = (d) => d.status === "accepted"
  ? `Accepted by the email provider for ${d.to}. Inbox delivery isn't confirmed.`
  : "Not sent: email is in test-capture mode on this environment. Nothing reached the recipient.";

const et = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + " ET" : "");

const SentLinks = ({ rows, onRefresh }) => (
  <div className="space-y-1" data-testid="case-share-sends">
    <div className="flex items-center justify-between"><p className="text-xs font-semibold uppercase tracking-wide text-[#5A6B7B]">Sent links (visible only to you)</p>
      <button type="button" onClick={onRefresh} className="text-xs text-[#1E3E62] underline" data-testid="case-share-sends-refresh">Refresh</button></div>
    {rows.map((r) => (
      <div key={r.token} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`case-share-send-${r.token}`}>
        <span className="text-[#0B192C]">{r.recipient?.channel === "sms" ? "Text" : "Email"} to {r.recipient?.to}</span>
        <span className="text-xs text-[#5A6B7B]">{r.send_status}{r.sent_at ? ` · ${et(r.sent_at)}` : ""}</span>
        {r.first_opened_at
          ? <span className="rounded-full bg-[#ECFDF3] px-2 py-0.5 text-xs font-semibold text-[#067647]" data-testid="case-share-opened">Opened · first {et(r.first_opened_at)} · latest {et(r.last_opened_at)}</span>
          : <span className="rounded-full bg-[#F4F1E8] px-2 py-0.5 text-xs text-[#5A4A1A]" data-testid="case-share-not-opened">Not opened</span>}
      </div>
    ))}
    <p className="text-[11px] text-[#8A8F98]">"Opened" means the comparison page loaded in a browser (link previews and scanners are ignored where we can). It doesn't prove who opened it.</p>
  </div>
);

export const HelmShareComparison = ({ caseId }) => {
  const [sends, setSends] = useState([]);
  const loadSends = useCallback(() => api.get("/agent/compare-shares", { params: { case_id: caseId } }).then((r) => setSends(r.data.sends || [])).catch(() => {}), [caseId]);
  useEffect(() => { loadSends(); }, [loadSends]);
  const [share, setShare] = useState(null);
  const [names, setNames] = useState(false);
  const [mode, setMode] = useState(null);
  const [to, setTo] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const ensure = async () => {
    if (share && share.names === names) return share;
    const d = (await api.post(`/agent/cases/${caseId}/share`, { include_names: names })).data;
    const s = { ...d, names };
    setShare(s);
    return s;
  };
  const open = async (m) => {
    setBusy(true); setResult(null);
    try {
      const s = await ensure();
      setMode(m); setTo("");
      setMsg(m === "text" ? `Hi, this is ${s.advisor_name || "your advisor"}. Here's the Medicare plan comparison we discussed: ${s.url}` : "");
      if (m === "copy") { await navigator.clipboard?.writeText(s.url).catch(() => {}); toast.success("Link copied."); }
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't create the link."); }
    setBusy(false);
  };
  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const d = mode === "text"
        ? (await api.post(`/agent/compare-shares/${share.token}/text`, { to, body: msg })).data
        : (await api.post(`/agent/compare-shares/${share.token}/email`, { to, note: msg })).data;
      setResult({ ok: mode === "text" ? !d.simulated : d.status === "accepted", text: mode === "text" ? smsResult(d) : emailResult(d) });
    } catch (e) { setResult({ ok: false, text: formatApiError(e?.response?.data?.detail) || "Not sent." }); }
    loadSends();
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-[#D9D6CC] bg-white p-4 space-y-3" data-testid="case-share">
      <p className="text-sm font-semibold text-[#0B192C]">Send this comparison (no household needed)</p>
      <label className="flex items-center gap-2 text-sm text-[#33475B]">
        <input type="checkbox" checked={names} onChange={(e) => { setNames(e.target.checked); setShare(null); }} data-testid="case-share-include-names" />
        Include doctor and medicine names (only when sending to the client themselves)
      </label>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={busy} onClick={() => open("text")} data-testid="case-share-text-btn"><MessageSquare className="mr-1 h-4 w-4" />Text comparison</Button>
        <Button variant="outline" disabled={busy} onClick={() => open("email")} data-testid="case-share-email-btn"><Mail className="mr-1 h-4 w-4" />Email comparison</Button>
        <Button variant="ghost" disabled={busy} onClick={() => open("copy")} data-testid="case-share-copy-btn"><Copy className="mr-1 h-4 w-4" />Copy link</Button>
      </div>
      {share && <p className="break-all text-xs text-[#5A6B7B]" data-testid="case-share-url">{share.url}</p>}
      {(mode === "text" || mode === "email") && (
        <div className="space-y-2" data-testid={`case-share-${mode}-form`}>
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder={mode === "text" ? "Recipient mobile number" : "Recipient email"} data-testid="case-share-to" />
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder={mode === "email" ? "Optional personal note (the link is added automatically)" : ""} data-testid="case-share-message" />
          <p className="text-xs text-[#5A6B7B]">{mode === "text" ? "Sent from your Nauticus number to your SBA prospect. Numbers that texted STOP are blocked; each recipient gets their own link." : "Sent by Nauticus email with your name and business number."}</p>
          <Button disabled={busy || !to.trim() || (mode === "text" && !msg.trim())} onClick={send} className="rounded-full bg-[#0B192C] text-white" data-testid="case-share-send">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "text" ? "Send text" : "Send email"}
          </Button>
        </div>
      )}
      {result && <p className={`text-sm ${result.ok ? "text-[#1F5132]" : "text-[#8A4B08]"}`} data-testid="case-share-result">{result.text}</p>}
      {sends.length > 0 && <SentLinks rows={sends} onRefresh={loadSends} />}
    </div>
  );
};
